import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { animate, motion, useMotionValue, useTransform } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { PRODUCTS, track } from "../data/products";
import { useLang } from "../i18n";
import { EASE, FadeIn, MaskLine, Micro } from "./Reveal";

const clamp = (min, v, max) => Math.min(Math.max(v, min), max);

export default function ProductRail() {
  const navigate = useNavigate();
  const { t } = useLang();
  const containerRef = useRef(null);
  const trackRef = useRef(null);
  const maxXRef = useRef(0);
  const lastDragRef = useRef(0);
  const [maxX, setMaxX] = useState(0);
  const x = useMotionValue(0);
  const progress = useTransform(x, [0, -(maxX || 1)], [0, 1]);

  useEffect(() => {
    const measure = () => {
      const c = containerRef.current;
      const t = trackRef.current;
      if (!c || !t) return;
      const m = Math.max(0, t.scrollWidth - c.clientWidth);
      maxXRef.current = m;
      setMaxX(m);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      const min = -maxXRef.current;
      if (min === 0) return;
      const cur = x.get();
      const canMove = e.deltaY > 0 ? cur > min + 1 : cur < -1;
      if (!canMove) return;
      e.preventDefault();
      animate(x, clamp(min, cur - e.deltaY * 1.4, 0), { duration: 0.7, ease: EASE });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [x]);

  const nudge = (dir) => {
    const min = -maxXRef.current;
    animate(x, clamp(min, x.get() + dir * 460, 0), { duration: 0.8, ease: EASE });
  };

  const open = (slug) => {
    if (Date.now() - lastDragRef.current < 300) return;
    track(`product:${slug}`);
    navigate(`/catalogue#${slug}`);
  };

  return (
    <section data-testid="showroom-section" className="py-24 md:py-36 border-t border-white/5 overflow-hidden">
      <div className="px-5 md:px-12 flex flex-wrap items-end justify-between gap-6 mb-12 md:mb-16">
        <div>
          <FadeIn>
            <Micro className="text-gold/80">{t("showroom.chapter")}</Micro>
          </FadeIn>
          <h2 className="font-display uppercase leading-[0.9] tracking-tight text-[clamp(2.2rem,5.5vw,4.6rem)] mt-5">
            <MaskLine delay={0.05}>{t("showroom.h1a")}</MaskLine>
            <MaskLine delay={0.15}>
              {t("showroom.h1b")} <span className="text-gold">{t("showroom.count")}</span>
            </MaskLine>
          </h2>
        </div>
        <FadeIn delay={0.2} className="flex items-end gap-6">
          <p className="hidden md:block text-sm text-cream/50 max-w-[220px]">
            {t("showroom.p")}
          </p>
          <div className="flex gap-2">
            <button
              data-testid="rail-prev"
              data-cursor="link"
              onClick={() => nudge(1)}
              aria-label={t("showroom.prev")}
              className="w-11 h-11 border border-white/15 flex items-center justify-center text-cream/60 hover:border-gold hover:text-gold transition-colors duration-300"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              data-testid="rail-next"
              data-cursor="link"
              onClick={() => nudge(-1)}
              aria-label={t("showroom.next")}
              className="w-11 h-11 border border-white/15 flex items-center justify-center text-cream/60 hover:border-gold hover:text-gold transition-colors duration-300"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </FadeIn>
      </div>

      <div ref={containerRef} data-cursor="drag" className="overflow-hidden">
        <motion.div
          ref={trackRef}
          drag="x"
          dragDirectionLock
          dragConstraints={{ left: -maxX, right: 0 }}
          dragElastic={0.05}
          onDragStart={() => x.stop()}
          onDragEnd={() => (lastDragRef.current = Date.now())}
          style={{ x }}
          className="flex gap-4 md:gap-5 w-max pl-5 md:pl-12 pr-5 md:pr-12 cursor-grab active:cursor-grabbing"
        >
          {PRODUCTS.map((p) => (
            <button
              key={p.slug}
              data-testid={`rail-card-${p.slug}`}
              onClick={() => open(p.slug)}
              className="group relative shrink-0 w-[76vw] sm:w-[360px] md:w-[400px] aspect-[3/4] max-h-[560px] overflow-hidden border border-white/10 bg-coal text-left hover:border-gold/50 transition-colors duration-500"
            >
              <img
                src={p.img}
                alt={p.name}
                loading="lazy"
                draggable="false"
                onError={(e) => (e.currentTarget.style.opacity = "0")}
                className="absolute inset-0 w-full h-full object-cover opacity-55 grayscale-[40%] transition duration-700 ease-out group-hover:scale-[1.06] group-hover:opacity-85 group-hover:grayscale-0"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/20 to-transparent" />

              <div className="absolute top-4 left-4 flex items-baseline gap-2">
                <span className="text-[10px] tracking-[0.3em] text-gold font-semibold">{p.num}</span>
                <span className="text-[10px] tracking-[0.3em] text-cream/30">/ 12</span>
              </div>
              <div className="absolute top-4 right-4">
                <span className="text-[9px] tracking-[0.25em] uppercase text-cream/40">{p.cat}</span>
              </div>

              <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
                <div className="font-display uppercase text-2xl md:text-3xl tracking-wide leading-tight transition-transform duration-500 group-hover:-translate-y-1">
                  {p.name}
                </div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-cream/40 mt-1.5">{p.full}</div>
                <div className="mt-4 flex items-center gap-2 text-gold text-[10px] tracking-[0.25em] uppercase opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition duration-500">
                  {t("showroom.view")} <ArrowRight className="w-3 h-3" />
                </div>
              </div>

              <span className="absolute bottom-0 left-0 h-[2px] w-full bg-gold scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500" />
            </button>
          ))}
        </motion.div>
      </div>

      <div className="px-5 md:px-12 mt-8">
        <div className="h-px bg-white/10 relative overflow-hidden">
          <motion.div
            data-testid="rail-progress"
            className="absolute inset-0 bg-gold origin-left"
            style={{ scaleX: progress }}
          />
        </div>
      </div>
    </section>
  );
}
